import { ReactNode, useState, useEffect, lazy, Suspense } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getConnectedUserRole } from '../utils';
import { IdeaContextProvider } from '../components/ideaNiv/IdeaContext';
import { FiltersContextProvider } from '../components/ideas/filters/context/FiltersContext';
import CustomerFiltersContextProvider from '../components/Customer360/filters/CustomerFiltersContextProvider';
import CustomerFiltersContextProviderV2 from '../components/Customer360V2/components/filtersv2/CustomerFiltersContextProvider';
import { IdeaPageProvider } from '../components/ideaNiv/IdeaPageContext';
import { SingleCustomerContextProvider } from '../components/Customer360/Customer-Context/SingleCustomerContext';
import { FiltersAndSegmentsContextContextProvider } from '../components/settings/filters-and-segments/FiltersAndSegmentsContext';
import { AdminProtectedRoute } from '../components/AdminProtectedRoute';
import { BagelLeadProtectedRoute } from '../components/BagelLeadProtectedRoute';
import { BagelAdminProtectedRoute } from '../components/bagel-admin-protected-route';
import { RoleProtectedRoute } from '../components/RoleProtectedRoute';
import { DiscoveryPageProvider } from '../components/explore/DiscoveryPageProvider';
import { FiltersProvider } from '../hooks/useFilters';
import { GTM_ROLES, IDEAS_VIEW_ROLES, VALID_APP_ROLES, FULL_MANAGER_ROLES } from '../hooks/useUserPermissions';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { ExploreProtectedRoute } from '../components/ExploreProtectedRoute';
import { useCurrentUser } from "../hooks/useCurrentUser";

// Lazy-loaded page components
const IdeasWraper = lazy(() => import('../components/ideas').then(m => ({ default: m.IdeasWraper })));
const EvidenceRouting = lazy(() => import('../components/EvidenceRouting'));
const DashboardWrapper = lazy(() => import('../components/dashboard').then(m => ({ default: m.DashboardWrapper })));
const NewIdeaView = lazy(() => import('../components/ideaNiv/NewIdeaView'));
const CustomerView = lazy(() => import('../components/Customer360'));
const Customer360V2Layout = lazy(() => import('../components/Customer360V2'));
const RoadmapViewPage = lazy(() => import('../components/Customer360V2/pages/RoadmapViewPage').then(m => ({ default: m.RoadmapViewPage })));
const EvidencesTablePage = lazy(() => import('../components/Customer360V2/pages/EvidencesTablePage').then(m => ({ default: m.EvidencesTablePage })));
const Account = lazy(() => import('../components/settings'));
const IntegrationsList = lazy(() => import('../components/settings/integrations2.0/pages/IntegrationsList').then(m => ({ default: m.IntegrationsList })));
const IntegrationDetail = lazy(() => import('../components/settings/integrations2.0/pages/IntegrationDetail').then(m => ({ default: m.IntegrationDetail })));
const CompleteIntegrationAuth = lazy(() => import('../components/settings/integrations2.0/pages/CompleteIntegrationAuth').then(m => ({ default: m.CompleteIntegrationAuth })));
const InternalItemsDetail = lazy(() => import('../components/settings/internal-items/pages/InternalItemsDetail').then(m => ({ default: m.InternalItemsDetail })));
const PIIRedactionSettings = lazy(() => import('../components/settings/pii-reduction'));
const MLSettings = lazy(() => import('../components/settings/ml-settings'));
const Teams = lazy(() => import('../components/settings/Teams'));
const Members = lazy(() => import('../components/settings/Members'));
const ImportIdeasEvidence = lazy(() => import('../components/ImportIdeasEvidence'));
const ImportItems = lazy(() => import('../components/ImportItems'));
const ExportIdeas = lazy(() => import('../components/ExportIdeas'));
const ExportItems = lazy(() => import('../components/ExportItems'));
const ProductAreas = lazy(() => import('../components/settings/ProductArea/ProductAreas'));
const TagsManager = lazy(() => import('../components/tags/TagsManager'));
const ReleasesManager = lazy(() => import('../components/releases/ReleasesManager'));
const PropertyManager = lazy(() => import('../components/settings/PropertyManager'));
const FilterGroupsSettingsContainer = lazy(() => import('../containers/filter-groups-settings-container/filter-groups-settings.container'));
const BagelObjects = lazy(() => import('../components/settings/BagelObjects').then(m => ({ default: m.BagelObjects })));
const ObjectSourcesPage = lazy(() => import('../components/settings/ObjectSourcesPage').then(m => ({ default: m.ObjectSourcesPage })));
const NewObjectSourcePage = lazy(() => import('../components/settings/NewObjectSourcePage').then(m => ({ default: m.NewObjectSourcePage })));
const Objects = lazy(() => import('../components/settings/objects/ObjectsAndFields'));
const Tokens = lazy(() => import('../components/settings/Tokens'));
const AuditLogs = lazy(() => import('../components/settings/audit-logs'));
const ObjectPage = lazy(() => import('../components/settings/objects/Object'));
const Idea = lazy(() => import('../components/settings/objects/Idea'));
const AuthCallback = lazy(() => import('../components/AuthCallback'));
const FiltersAndSegmentes = lazy(() => import('../components/settings/filters-and-segments'));
const Notifications = lazy(() => import('../components/settings/notifications'));
const AiIdeaPage = lazy(() => import('../pages/AiIdeaPage'));
const FixLogs = lazy(() => import('../components/settings/admin/FixLogs'));
const Evidences = lazy(() => import('../components/evidences'));
const FieldConfiguration = lazy(() => import('../components/settings/FieldConfiguration'));

export interface RouteConfig {
  path: string;
  element: ReactNode;
  key?: string;
  children?: RouteConfig[];
}

// Custom hook to manage ideaAi localStorage value
const useIdeaAiPreference = () => {
  const [newPage, setNewPage] = useState<boolean>(true);

  useEffect(() => {
    // Check if ideaAi exists in localStorage
    const newPage = localStorage.getItem('newPage');

    if (newPage === null) {
      // If it doesn't exist, save the default value (true)
      localStorage.setItem('newPage', 'true');
      setNewPage(true);
    } else {
      // If it exists, load the saved value
      setNewPage(newPage === 'true');
    }
  }, []);

  return newPage;
};

const InboxRedirect: React.FC = () => {
  const location = useLocation();
  const search = location.search || '';
  return <Navigate to={`/evidences/inbox${search}`} replace />;
};

// Landing redirect: agent or GTM → customer360, else → /ideas
const getLandingPath = (userRole: string) =>
  GTM_ROLES.includes(userRole) ? '/customer360' : '/ideas/all';

export const DefaultRouteRedirect: React.FC = () => {
  const currentUser = useCurrentUser();
  const userRole = getConnectedUserRole(currentUser);
  const { canSeeExplore, isExploreAccessResolved } = useCurrentUserProfile();

  if (!isExploreAccessResolved) {
    return null;
  }

  if (canSeeExplore) {
    return <Navigate to="/explore" replace />;
  }

  return <Navigate to={getLandingPath(userRole)} replace />;
};


// Function to get base routes based on ideaAi preference
const getBaseRoutes = (newPage: boolean): RouteConfig[] => [
  {
    path: '/',
    element: <DefaultRouteRedirect />,
  },
  {
    path: '/inbox',
    element: <InboxRedirect />,
  },
  {
    path: '/evidences/:pageKey?',
    element: <RoleProtectedRoute allowedRoles={FULL_MANAGER_ROLES}> <Suspense fallback={null}><Evidences /></Suspense> </RoleProtectedRoute>,
  },
  {
    path: '/ideas',
    element: <RoleProtectedRoute allowedRoles={IDEAS_VIEW_ROLES}> <Suspense fallback={null}><IdeasWraper pageKey={null} /></Suspense> </RoleProtectedRoute>,
  },
  {
    path: '/evidence/:id',
    element: <Suspense fallback={null}><EvidenceRouting /></Suspense>,
  },
  {
    path: '/evidence/:id/comment/:commentId',
    element: <Suspense fallback={null}><EvidenceRouting /></Suspense>,
  },
  {
    path: '/evidence',
    element: <Suspense fallback={null}><EvidenceRouting /></Suspense>,
  },
  {
    path: '/ideas/all',
    element: (
      <RoleProtectedRoute allowedRoles={IDEAS_VIEW_ROLES}>
        <Suspense fallback={null}><IdeasWraper pageKey={null} /></Suspense>
      </RoleProtectedRoute>
    ),
  },
  {
    path: '/ideas/archived',
    element: (
      <RoleProtectedRoute allowedRoles={IDEAS_VIEW_ROLES}>
        <Suspense fallback={null}><IdeasWraper pageKey="archived" /></Suspense>
      </RoleProtectedRoute>
    ),
  },
  {
    path: '/trends',
    element: (
      <RoleProtectedRoute allowedRoles={IDEAS_VIEW_ROLES}>
        <Suspense fallback={null}><DashboardWrapper/></Suspense>
      </RoleProtectedRoute>
    ),
  },
  // Conditional route based on ideaAi preference
  ...(newPage ?   [
    {
      path: '/idea/:id',
      element:
      <RoleProtectedRoute allowedRoles={IDEAS_VIEW_ROLES}>
        <FiltersProvider>
          <Suspense fallback={null}><AiIdeaPage /></Suspense>
        </FiltersProvider>
      </RoleProtectedRoute>
    }
  ]:
    [
      {
        path: '/idea/:id',
        element: (
          <RoleProtectedRoute allowedRoles={IDEAS_VIEW_ROLES}>
            <IdeaContextProvider>
              <FiltersContextProvider>
                <IdeaPageProvider>
                  <Suspense fallback={null}><NewIdeaView /></Suspense>
                </IdeaPageProvider>
              </FiltersContextProvider>
            </IdeaContextProvider>
          </RoleProtectedRoute>
        )
      }
    ]
  ),
  {
    path: '/discovery',
    element: (
      <RoleProtectedRoute allowedRoles={VALID_APP_ROLES}>
        <ExploreProtectedRoute>
          <DiscoveryPageProvider />
        </ExploreProtectedRoute>
      </RoleProtectedRoute>
    ),
  },
  {
    path: '/explore',
    element: (
      <RoleProtectedRoute allowedRoles={VALID_APP_ROLES}>
        <ExploreProtectedRoute>
          <DiscoveryPageProvider />
        </ExploreProtectedRoute>
      </RoleProtectedRoute>
    ),
  },
  {
    path: '/customer360',
    element: (
      <RoleProtectedRoute allowedRoles={VALID_APP_ROLES}>
        <CustomerFiltersContextProviderV2>
          <Suspense fallback={null}><Customer360V2Layout /></Suspense>
        </CustomerFiltersContextProviderV2>
      </RoleProtectedRoute>
    ),
    children: [
      { path: 'roadmap-view', element: <Suspense fallback={null}><RoadmapViewPage /></Suspense> },
      { path: 'evidences-table', element: <Suspense fallback={null}><EvidencesTablePage /></Suspense> },
    ],
  },
  {
    path: '/customer360/:id',
    element: (
      <RoleProtectedRoute allowedRoles={VALID_APP_ROLES}>
        <SingleCustomerContextProvider>
          <CustomerFiltersContextProvider>
            <Suspense fallback={null}><CustomerView /></Suspense>
          </CustomerFiltersContextProvider>
        </SingleCustomerContextProvider>
      </RoleProtectedRoute>
    ),
  },
];

// Settings routes
const settingsRoutes: RouteConfig[] = [
  {
    path: '/settings',
    element: <AdminProtectedRoute><Suspense fallback={null}><Account /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/account',
    element: <AdminProtectedRoute><Suspense fallback={null}><Account /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/integrations2.0',
    element: <AdminProtectedRoute><Suspense fallback={null}><IntegrationsList /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/integrations2.0/:key',
    element: <AdminProtectedRoute><Suspense fallback={null}><IntegrationDetail /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/integrations2.0/:key/complete',
    element: <AdminProtectedRoute><Suspense fallback={null}><CompleteIntegrationAuth /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/internal-items',
    element: <AdminProtectedRoute><Suspense fallback={null}><InternalItemsDetail /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/pii-redaction',
    element: <AdminProtectedRoute><Suspense fallback={null}><PIIRedactionSettings /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/ml-settings',
    element: <AdminProtectedRoute><Suspense fallback={null}><MLSettings /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/teams',
    element: <AdminProtectedRoute><Suspense fallback={null}><Teams /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/members',
    element: <AdminProtectedRoute><Suspense fallback={null}><Members /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/import/ideas_evidence',
    element: <AdminProtectedRoute><Suspense fallback={null}><ImportIdeasEvidence /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/import/items',
    element: <AdminProtectedRoute><Suspense fallback={null}><ImportItems /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/export/ideas',
    element: <AdminProtectedRoute><Suspense fallback={null}><ExportIdeas /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/export/items',
    element: <AdminProtectedRoute><Suspense fallback={null}><ExportItems /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/productareas',
    element: <AdminProtectedRoute><Suspense fallback={null}><ProductAreas /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/tags',
    element: <AdminProtectedRoute><Suspense fallback={null}><TagsManager /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/releases',
    element: <AdminProtectedRoute><Suspense fallback={null}><ReleasesManager /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/property',
    element: <AdminProtectedRoute><Suspense fallback={null}><PropertyManager /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/strategic-scopes',
    element: <BagelAdminProtectedRoute><Suspense fallback={null}><FilterGroupsSettingsContainer /></Suspense></BagelAdminProtectedRoute>,
  },
  {
    path: '/settings/bagel-objects',
    element: <AdminProtectedRoute><Suspense fallback={null}><BagelObjects /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/bagel-objects/:objectKey',
    element: <AdminProtectedRoute><Suspense fallback={null}><ObjectSourcesPage /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/bagel-objects/:objectKey/new-source',
    element: <AdminProtectedRoute><Suspense fallback={null}><NewObjectSourcePage /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/objects',
    element: <AdminProtectedRoute><Suspense fallback={null}><Objects /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/filters',
    element: <AdminProtectedRoute>
      <FiltersAndSegmentsContextContextProvider>
        <Suspense fallback={null}><FiltersAndSegmentes /></Suspense>
      </FiltersAndSegmentsContextContextProvider>
    </AdminProtectedRoute>,
  },
  {
    path: '/settings/tokens',
    element: <AdminProtectedRoute><Suspense fallback={null}><Tokens /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/audit-logs',
    element: <AdminProtectedRoute><Suspense fallback={null}><AuditLogs /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/notifications',
    element:<AdminProtectedRoute><Suspense fallback={null}><Notifications /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/admin/fixes',
    element: <BagelLeadProtectedRoute><Suspense fallback={null}><FixLogs /></Suspense></BagelLeadProtectedRoute>,
  },
  {
    path: '/settings/field-configuration',
    element: <AdminProtectedRoute><Suspense fallback={null}><FieldConfiguration /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/objects/:slug',
    element: <AdminProtectedRoute><Suspense fallback={null}><ObjectPage /></Suspense></AdminProtectedRoute>,
  },
  {
    path: '/settings/objects/idea/:ideaObject',
    element: <AdminProtectedRoute><Suspense fallback={null}><Idea /></Suspense></AdminProtectedRoute>,
  },
];

// Additional routes
const additionalRoutes: RouteConfig[] = [
  {
    path: '/newIntegration',
    element: <Suspense fallback={null}><CompleteIntegrationAuth /></Suspense>,
  },
  {
    path: '/postLogin',
    element: <Suspense fallback={null}><AuthCallback /></Suspense>,
  },
];

// Component that provides routes with ideaAi preference
export const RoutesProvider = () => {
  const oldPage = useIdeaAiPreference();

  return {
    routes: [
      ...getBaseRoutes(oldPage),
      ...settingsRoutes,
      ...additionalRoutes,
    ]
  };
};

// For backward compatibility, export routes without the preference
export const routes: RouteConfig[] = [
  ...getBaseRoutes(true), // Default to true for backward compatibility
  ...settingsRoutes,
  ...additionalRoutes,
];